import * as React from 'react';
import Image from 'next/image'; // Ensure the correct Image component is imported
import { getOr } from '@/utils/options';
import { ItemsList, Maybe } from 'types/generated';
import clsx from 'clsx';
import Button from 'components/src/button';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { ArrowLeftIcon } from 'components/src/svg';
import { Label } from 'components/src/typography';
import { bezierEase } from '@/utils/utils';

type Props = {
  data: ItemsList;
  showMobileMenu?: boolean;
  setOpenDropdownTitle: (title: string | null) => void;
  openDropdownTitle?: string | null;
  setOpenList?: (isOpen: boolean) => void;
  isOpen: boolean;
};

const NavbarDropdownList = ({
  isOpen,
  showMobileMenu = false,
  data,
  setOpenDropdownTitle,
  openDropdownTitle,
  setOpenList,
}: Props) => {
  const router = useRouter();

  const navigateToItem = (uri: Maybe<string> | undefined) => {
    if (uri) {
      router.push(uri);
      setOpenList && setOpenList(false);
      setOpenDropdownTitle(null);
    }
  };
  React.useEffect(() => {
    if (openDropdownTitle) {
      setOpenList && setOpenList(false);
    }
  }, [openDropdownTitle, setOpenList]);

  const items = data?.itemCollection?.items;
  const options = data?.optionsCollection?.items;
  const secondaryItems = data?.secondaryItemsCollection?.items;

  const MotionLabel = motion.create(Label);

  const handleBackClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setOpenList && setOpenList(false);
  };

  return (
    <>
      {showMobileMenu && (
        <div className="flex flex-row flex-wrap content-center items-center gap-xs self-stretch py-4">
          <button className="px-md flex flex-1 gap-sm py-[10px]" onClick={(e) => handleBackClick(e)}>
            <ArrowLeftIcon />
            <MotionLabel whileHover={{ textDecoration: 'underline' }} classes="label-lg text-red-100">
              {getOr(data.id, '')}
            </MotionLabel>
          </button>
        </div>
      )}
      <motion.div
        initial={{ height: 'auto', opacity: 0 }}
        animate={
          isOpen ? { height: 'auto', opacity: 1, transition: { ease: bezierEase, duration: 0.24 } } : { height: 'auto' }
        }
        exit={
          isOpen ? { height: 'auto', opacity: 0, transition: { ease: bezierEase, duration: 0.24 } } : { height: 'auto' }
        }
        className={clsx('flex h-auto flex-col overflow-hidden', {
          'absolute top-[90px] -translate-x-2/3 rounded-xl bg-white-50': !showMobileMenu,
          'max-h-[calc(100vh-200px)] self-stretch overflow-scroll rounded-lg bg-red-100': showMobileMenu,
          'pointer-events-none': !isOpen && !showMobileMenu,
        })}
        style={
          !showMobileMenu
            ? {
                boxShadow:
                  '0px 727px 203px 0px rgba(209, 0, 31, 0.00), 0px 465px 186px 0px rgba(209, 0, 31, 0.01), 0px 262px 157px 0px rgba(209, 0, 31, 0.05), 0px 116px 116px 0px rgba(209, 0, 31, 0.09), 0px 29px 64px 0px rgba(209, 0, 31, 0.10)',
              }
            : {}
        }
      >
        <div
          className={clsx('align-start flex flex-col items-start gap-8 p-lg', {
            'self-stretch': showMobileMenu,
            'w-[305px]': !showMobileMenu,
          })}
        >
          {items?.map((item, index) => {
            if (!item) {
              return null;
            }
            return (
              <Button
                onClick={() => navigateToItem(item.uri)}
                label={getOr(item.title, '')}
                key={index}
                variant={showMobileMenu ? 'primary' : 'surface'}
                labelClasses="text-red-100 self-stretch"
                classes={clsx('flex h-auto w-auto items-center !justify-start self-stretch px-xl py-[18px]', {
                  'flex-1 justify-start gap-sm self-stretch py-[18px]': showMobileMenu,
                })}
              />
            );
          })}
        </div>
        {options && options.length > 0 && (
          <div
            className={clsx('align-start flex flex-col items-start gap-8 border-t border-solid p-lg', {
              'w-[305px] border-[#E1E2E3]': !showMobileMenu,
              'border-[#F58E9A]': showMobileMenu,
            })}
          >
            {options?.map((option, index) => {
              if (!option) {
                return null;
              }
              return (
                <Button
                  label={getOr(option.title, '')}
                  icon={
                    option.icon?.url ? (
                      <Image src={getOr(option.icon?.url, '')} alt="Option Icon" width={24} height={24} />
                    ) : undefined
                  }
                  key={index}
                  variant={showMobileMenu ? 'primary' : 'surface'}
                  labelClasses="text-red-100 self-stretch pt-2"
                  classes={clsx('flex h-auto w-auto items-center !justify-start self-stretch px-xl py-[18px]', {
                    'flex-1 justify-start gap-sm self-stretch py-[18px]': showMobileMenu,
                  })}
                />
              );
            })}
          </div>
        )}
        <div
          className={clsx('align-start flex w-[305px] flex-col items-start gap-8 p-lg', {
            'border-t border-solid border-[#E1E2E3]': !showMobileMenu,
          })}
        >
          {secondaryItems?.map((item, index) => {
            if (!item) {
              return null;
            }
            return (
              <Button
                onClick={() => navigateToItem(item.uri)}
                label={getOr(item.title, '')}
                key={index}
                variant={showMobileMenu ? 'primary' : 'surface'}
                labelClasses="text-red-100 self-stretch"
                classes={clsx(
                  'align-center flex h-auto w-auto items-center !justify-start self-stretch px-xl py-[18px]',
                  {
                    'flex-1 justify-start gap-sm self-stretch py-[18px]': showMobileMenu,
                  }
                )}
              />
            );
          })}
        </div>
      </motion.div>
    </>
  );
};

export default NavbarDropdownList;
